// SPDX-License-Identifier: AGPL-3.0-or-later
import { firstAnswer, walkQuestionnaireItems } from './answers.js';
/**
 * FHIR R4 `enableWhen` / `enableBehavior` evaluation.
 *
 * Reference: https://www.hl7.org/fhir/R4/questionnaire-definitions.html#Questionnaire.item.enableWhen
 *
 * Returns the set of `linkId`s that are *active* (visible / submittable)
 * given the current QuestionnaireResponse. Inactive items are skipped by
 * every later pass.
 */
export function resolveActiveItems(questionnaire, response) {
    const active = new Set();
    function visit(items, parentActive) {
        if (items === undefined)
            return;
        for (const item of items) {
            const itemActive = parentActive && isItemEnabled(item, response);
            if (itemActive)
                active.add(item.linkId);
            visit(item.item, itemActive);
        }
    }
    visit(questionnaire.item, true);
    return active;
}
export function isItemEnabled(item, response) {
    const conditions = item.enableWhen;
    if (conditions === undefined || conditions.length === 0)
        return true;
    const behavior = item.enableBehavior ?? 'any';
    if (behavior === 'all') {
        return conditions.every((c) => evaluateEnableWhen(c, response));
    }
    return conditions.some((c) => evaluateEnableWhen(c, response));
}
export function evaluateEnableWhen(condition, response) {
    const actual = firstAnswer(response, condition.question);
    const expected = expectedValue(condition);
    switch (condition.operator) {
        case 'exists': {
            const wantPresent = expected === true || expected === null;
            const present = actual !== undefined && actual !== null;
            // The expected value is a boolean — `answerBoolean: true` means require presence.
            return wantPresent ? present : !present;
        }
        case '=':
            return looseEqual(actual, expected);
        case '!=':
            return !looseEqual(actual, expected);
        case '<':
            return numericCompare(actual, expected) < 0;
        case '<=':
            return numericCompare(actual, expected) <= 0;
        case '>':
            return numericCompare(actual, expected) > 0;
        case '>=':
            return numericCompare(actual, expected) >= 0;
    }
}
function expectedValue(condition) {
    if (condition.answerBoolean !== undefined)
        return condition.answerBoolean;
    if (condition.answerDecimal !== undefined)
        return condition.answerDecimal;
    if (condition.answerInteger !== undefined)
        return condition.answerInteger;
    if (condition.answerDate !== undefined)
        return condition.answerDate;
    if (condition.answerDateTime !== undefined)
        return condition.answerDateTime;
    if (condition.answerTime !== undefined)
        return condition.answerTime;
    if (condition.answerString !== undefined)
        return condition.answerString;
    if (condition.answerCoding !== undefined)
        return condition.answerCoding.code ?? null;
    return undefined;
}
function looseEqual(a, b) {
    const na = a === undefined ? null : a;
    const nb = b === undefined ? null : b;
    return na === nb;
}
function numericCompare(a, b) {
    const na = toNumeric(a);
    const nb = toNumeric(b);
    if (Number.isNaN(na) || Number.isNaN(nb))
        return Number.NaN;
    if (na < nb)
        return -1;
    if (na > nb)
        return 1;
    return 0;
}
function toNumeric(v) {
    if (typeof v === 'number')
        return v;
    if (typeof v === 'boolean')
        return v ? 1 : 0;
    if (v === null || v === undefined)
        return Number.NaN;
    if (typeof v === 'string') {
        if (v.trim() === '')
            return Number.NaN;
        return Number(v);
    }
    return Number.NaN;
}
/** Flatten every item in a Questionnaire to an index by `linkId`. */
export function indexQuestionnaireItems(questionnaire) {
    const out = new Map();
    for (const item of walkQuestionnaireItems(questionnaire.item)) {
        out.set(item.linkId, item);
    }
    return out;
}
//# sourceMappingURL=conditional.js.map